import { getFileName, serialize } from "../../helpers/utils/Utils";
import { tgxApi } from "../../app/tgxApi";
import {
    getAuthorizationHeader,
    getFileDownloadHeaders,
    handleQueryError,
    handleQueryErrorAndSuccess
} from "../../helpers/utils/RtkQueryUtils";
import { downloadFile } from "../../helpers/utils/FileUtils";

export const productsApi = tgxApi.injectEndpoints({

    endpoints: (build) => ({
        getProductById: build.query({
            query: (id) => {
                return {
                    url: `/products/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ["productById"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        searchOrderResulHistoryByProductId: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/ajax/order-result-history?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
            providesTags: ["products"]
        }),

        searchProducts: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/products/search?${paramData}`,
                    method: 'POST',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
            providesTags: ["products"]
        }),


        clonedAccounts: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/editor/cloneAccounts?${paramData}`,
                    method: 'POST',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
            providesTags: ["products"]
        }),

        getTeamAndUserAssignments: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/ajax/my-team-orders?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ["teamAndUserAssignments"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        updatePropertySkipAutomation: build.mutation({
            query: (propertyResultId) => {
                return {
                    url: `/ajax/update-property-skip-automation/${propertyResultId}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["productById", 'orderById','getOrderByOrderIdAndType'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Skip Automation ")
            },
        }),

        getProductPricingById: build.query({
            query: (id) => {
                return {
                    url: `/product-pricings/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
            providesTags: ["productPricing"],
        }),

        getProductPricingByProductId: build.query({
            query: (id) => {
                return {
                    url: `/product-pricings/product/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
            providesTags: ["productPricing"],
        }),

        updateProductPricing: build.mutation({
            query: ({ pricingId, body }) => {
                return {
                    url: `/product-pricings/${pricingId}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: body
                };
            },
            invalidatesTags: ["productPricing"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Product Pricing")
            },
        }),

        startProductFollowUp: build.mutation({
            query: ({ productId, followUpObj }) => {
                return {
                    url: `/products/${productId}/start-follow-up`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: followUpObj
                };
            },
            invalidatesTags: ["productById", "products", "teamAndUserAssignments"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        endProductFollowUp: build.mutation({
            query: (productId) => {
                return {
                    url: `/products/${productId}/close-follow-up`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ["getOrderByOrderIdAndType", "productById", "followUpTypeById", "products", "teamAndUserAssignments"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        updateAssignmentInfo: build.mutation({
            query: ({ productId, assigneeId, assigneeType, reason }) => {
                return {
                    url: `/products/${productId}/assignment-info?${serialize({
                        assigneeId: assigneeId,
                        assigneeType: assigneeType,
                        reason: reason
                    })}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ["productById", "assignmentLogs", "getOrderResultByOrderId"],
            async onQueryStarted({ assigneeId }, { queryFulfilled, }) {
                if (assigneeId) {
                    await handleQueryErrorAndSuccess(queryFulfilled, "Assigned", "Product")
                } else {
                    await handleQueryErrorAndSuccess(queryFulfilled, "UnAssigned", "Product")
                }
            },
        }),

        assignProduct: build.mutation({
            query: ({ productId, userId, isAssigned }) => {
                return {
                    url: `/ajax/assign-product/${productId}/${userId}/${isAssigned}`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ["productById", "assignmentLogs", "getOrderByOrderIdAndType", "getOrderResultByOrderId"],
            async onQueryStarted({ assigneeId }, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Assigned", "Product")
            },
        }),

        updateProductTargetTeamId: build.mutation({
            query: ({ productId, teamId }) => {
                return {
                    url: `/ajax/update-product-team-id/${productId}/${teamId}`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ["productById", "assignmentLogs", "getOrderByOrderIdAndType"],
            async onQueryStarted({ assigneeId }, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Assigned", "Product")
            },
        }),

        getAssignmentLogs: build.query({
            query: (id) => {

                return {
                    url: `/ajax/get-order-assignment-logs-by-order-id/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ["assignmentLogs"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        getUnassignedTeamWork: build.query({
            query: (data) => {
                let paramData = serialize(data);
                return {
                    url: `ajax/user/teams/unassigned-products?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        assignProductToUser: build.mutation({
            query: () => {
                return {
                    url: `/ajax/get-next-order`,
                    method: 'POST',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["getMyOrders"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        findAllInvoiceLines: build.query({
            query: (productId) => {
                return {
                    url: `/products/invoice-lines?productId=${productId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
            providesTags: ["invoiceLines"]
        }),

        findDefaultInvoiceLines: build.query({
            query: (productId) => {
                return {
                    url: `/products/default-invoice-lines?productId=${productId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
            providesTags: ["defaultInvoiceLines"]
        }),

        findAllInvoiceNotes: build.query({
            query: (productId) => {
                return {
                    url: `/products/invoice-notes?productId=${productId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
            providesTags: ["invoiceNotes"]
        }),

        exportProductData: build.query({
            query: (query) => {
                let paramData = serialize(query);
                return {
                    url: `/export/order?${paramData}`,
                    method: 'POST',
                    headers: getFileDownloadHeaders(),
                    responseType: 'arraybuffer',
                    responseHandler: async (response: { blob: () => any; }) => {
                        const blob = await response.blob();
                        downloadFile(blob, getFileName("orders"))
                        return { success: true };
                    },
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Exported", "Order Data")
            },
        }),

        getOrderLogList: build.query({
            query: ({ productId, logTypes }) => {
                return {
                    url: `/ajax/get-order-logs-by-order-id/${productId}?${serialize({ logTypes: logTypes })}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['orderLogs'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        getLinkedAccountsService: build.query({
            query: ({ primaryCollectorId, accountIds }) => {
                let param = serialize({ primaryCollectorId: primaryCollectorId, accountIds: accountIds })
                return {
                    url: `/editor/linkedAccounts?${param}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),


        getOrderNoteList: build.query({
            query: (productId) => {
                return {
                    url: `/ajax/get-order-note-by-order-id/${productId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ["orderNoteList"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getOrderToDoList: build.query({
            query: (productId) => {
                return {
                    url: `/ajax/get-todo-task-list/${productId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ["orderToDoList"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getProductTaskList: build.query({
            query: (productId) => {
                return {
                    url: `/editor/taskList/${productId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ["productTaskList"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getQCLogicResultList: build.query({
            query: (productId) => {
                return {
                    url: `/editor/qc-logic-result/${productId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ["qcLogicResultList"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getOrderNoteByTypeAndId: build.query({
            query: ({ orderId, noteTypes }) => {
                let typesList = { noteTypes: noteTypes };
                let param = serialize(typesList);
                return {
                    url: `/ajax/get-order-note-by-order-type/${orderId}?${param}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ["orderNoteList"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getMyOrderList: build.query({
            query: (data) => {
                let paramData = serialize(data);
                return {
                    url: `/ajax/get-my-order?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ["getMyOrders"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        createNote: build.mutation({
            query: ({ productId, requestBody, param }) => {
                let paramData = serialize(param);

                return {
                    url: `/product-notes/${productId}?${paramData}`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ["orderNoteList", "cannedNotes"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Order Note")
            },
        }),


        updateNoteVisibility: build.mutation({
            query: ({ productId, id }) => {
                return {
                    url: `/ajax/update-note-visibility/${productId}/${id}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["orderNoteList"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Order Note")
            },
        }),

        updateProductOverridePrice: build.mutation({
            query: ({ productId, overridePrice, overrideDisplayPrice }) => {
                let params = serialize({ overridePrice: overridePrice, overrideDisplayPrice: overrideDisplayPrice });
                return {
                    url: `/ajax/update-product-override-price/${productId}?${params}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["orderById", "getOrderByOrderIdAndType"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Product Override Price")
            },
        }),

        updateToDoCompletionStatus: build.mutation({
            query: (toDoId) => {
                return {
                    url: `/ajax/update-todo-completion-status/${toDoId}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["orderToDoList"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "To Do Status   ")
            },
        }),

        removeToDo: build.mutation({
            query: (toDoId) => {
                return {
                    url: `/ajax/remove-todo/${toDoId}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["orderToDoList"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "Order To Do List")
            },
        }),

        createToDoTask: build.mutation({
            query: (obj) => {
                return {
                    url: `/ajax/create-todo-task`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: obj
                };
            },
            invalidatesTags: ["orderToDoList"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Order To Do List")
            },
        }),
        updateToDoTask: build.mutation({
            query: ({ toDoId, obj }) => {
                return {
                    url: `/ajax/update-todo-task/${toDoId}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: obj
                };
            },
            invalidatesTags: ["orderToDoList"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Order To Do List")
            },
        }),

        createNoteByOrderId: build.mutation({
            query: ({ productId, requestBody }) => {
                return {
                    url: `/ajax/create-order-note/${productId}/notes`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ["orderNoteList", "cannedNotes"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Order Note")
            },
        }),
        updateCustomerHold: build.mutation({
            query: (orderId) => {
                return {
                    url: `/ajax/${orderId}/customer-hold`,
                    method: 'PUT',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["getOrderByOrderIdAndType"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Customer Hold")
            },
        }),
        updateMarkAsCustomerRetrieved: build.mutation({
            query: (orderId) => {
                return {
                    url: `/ajax/${orderId}/markAsCustomerRetrieved`,
                    method: 'PUT',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["getOrderByOrderIdAndType"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Marked as Retrieved!")
            },
        }),

        updateUnLockedProductsFromCustomer: build.mutation({
            query: (productIds) => {
                return {
                    url: `/ajax/unlock-from-customer?productIds=${productIds}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Locked", "product to customer!")
            },
        }),

        cancelOrder: build.mutation({
            query: (productId) => {
                return {
                    url: `/ajax/cancelOrder/${productId}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["getOrderByOrderIdAndType"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Canceled", "Order");
            },
        }),
        cancelOrderOnVendor: build.mutation({
            query: (taskId) => {
                return {
                    url: `/ajax/cancel-order-on-vendor/${taskId}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            },
        }),
        deleteNoteService: build.mutation({
            query: ({ orderId, noteId }) => {
                return {
                    url: `/ajax/delete-note/${orderId}/${noteId}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            }, async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Delete", "Notes");
            },
        }),

        markAsIssueOrder: build.mutation({
            query: (productId) => {
                return {
                    url: `/ajax/changeOrderStatus/${productId}/ISSUE`,
                    method: 'PUT',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["getOrderByOrderIdAndType"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled ,"Marked" ,"as issue");
            },
        }),

        markAsRushOrder: build.mutation({
            query: (productId) => {
                return {
                    url: `/ajax/${productId}/markAsRushOrder`,
                    method: 'PUT',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["getOrderByOrderIdAndType"],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Marked", "as rush");
            },
        }),

        getOrderListByCustomer: build.query({
            query: ({ customerId, customerOrderNumber }) => {

                let newCustomerOrderNumber = customerOrderNumber?.replace(/#/g, '%23');

                return {
                    url: `/ajax/get-order-list/${newCustomerOrderNumber}/${customerId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled);
            },
        }),
        getPropertyPickerResult: build.mutation({
            query: ({ list, orderPropertyId }) => {
                let paramData = serialize({ orderPropertyId: orderPropertyId });

                return {
                    url: `/editor/property-picker-data?${paramData}`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: list
                };

            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "AccountsLinked");
            },
        }),


    }),
});

export const {
    useGetProductByIdQuery,
    useGetProductPricingByIdQuery,
    useUpdateProductPricingMutation,
    useStartProductFollowUpMutation,
    useEndProductFollowUpMutation,
    useGetTeamAndUserAssignmentsQuery,
    useAssignProductToUserMutation,
    useUpdateAssignmentInfoMutation,
    useGetAssignmentLogsQuery,
    useLazyExportProductDataQuery,
    useGetOrderNoteListQuery,
    useGetOrderLogListQuery,
    useUpdateNoteVisibilityMutation,
    useCreateNoteMutation,
    useGetMyOrderListQuery,
    useGetUnassignedTeamWorkQuery,
    useUpdateCustomerHoldMutation,
    useCancelOrderMutation,
    useMarkAsIssueOrderMutation,
    useMarkAsRushOrderMutation,
    useGetOrderNoteByTypeAndIdQuery,
    useGetProductTaskListQuery,
    useLazyGetProductTaskListQuery,
    useGetQCLogicResultListQuery,
    useLazyGetQCLogicResultListQuery,
    useLazyGetLinkedAccountsServiceQuery,
    useDeleteNoteServiceMutation,
    useCancelOrderOnVendorMutation,
    useCreateNoteByOrderIdMutation,
    useClonedAccountsQuery,
    useLazyGetOrderListByCustomerQuery,
    useAssignProductMutation,
    useUpdateProductTargetTeamIdMutation,
    useUpdateMarkAsCustomerRetrievedMutation,
    useUpdateUnLockedProductsFromCustomerMutation,
    useGetOrderToDoListQuery,
    useUpdateToDoCompletionStatusMutation,
    useRemoveToDoMutation,
    useCreateToDoTaskMutation,
    useUpdateToDoTaskMutation,
    useSearchOrderResulHistoryByProductIdQuery,
    useUpdateProductOverridePriceMutation,
    useGetPropertyPickerResultMutation,
    useUpdatePropertySkipAutomationMutation
} = productsApi;